import { useState, useCallback, useMemo, useEffect } from 'react'
import { map, get, compact } from 'lodash'
import { useParams, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSnackbar } from 'notistack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { useTeam } from '../../../contexts/TeamContext'
import { useProject } from '../../../contexts/ProjectContext'
import { useConfirmationDialog } from '../../../system/ConfirmationDialogProvider'
import { useNodes } from '../../../contexts/NodesContext'
import useComposeResource from '../../../shared/resources/api/v1/admin/docker/useComposeResource'
import useDeploymentsResources from '../../../shared/resources/api/v1/admin/docker/useDeploymentsResources'
import useIngressControllersResources from '../../../shared/resources/api/v1/admin/docker/useIngressControllersResources'
import useProjectNetworksResources from '../../../shared/resources/api/v1/admin/docker/useProjectNetworksResources'
import useProjectFilesResources from '../../../shared/resources/api/v1/admin/docker/useProjectFilesResources'
import useProjectsResources from '../../../shared/resources/api/v1/admin/docker/useProjectsResources'
import LoadingState from '../../../shared/components/LoadingState'
import ErrorState from '../../../shared/components/ErrorState'
import PageHeader from '../../../shared/components/layouts/PageHeader'
import PageBody from '../../../shared/components/layouts/PageBody'
import TabPanel from '../../../shared/components/TabPanel'
import ResponsiveTabs from '../../../shared/components/ResponsiveTabs'
import ProjectServicesTab from './components/ProjectServicesTab'
import ProjectIngressControllersTab from './components/ProjectIngressControllersTab'
import ProjectDeploymentsTab from './components/ProjectDeploymentsTab'
import ProjectYamlTab from './components/ProjectYamlTab'
import ProjectNetworksTab from './components/ProjectNetworksTab'
import ProjectFilesTab from './components/ProjectFilesTab'
import Flex from "../../../shared/components/layouts/Flex.jsx";

function ProjectShowPage() {
  const { teamSlug, projectSlug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const { team } = useTeam()
  const { project, services, deployments, ingressControllers, loading, deploymentsLoading, ingressControllersLoading, error, reloadIngressControllers } = useProject()
  const { get: getCompose, update: updateCompose } = useComposeResource(teamSlug, projectSlug)
  const { create: createDeployment } = useDeploymentsResources(teamSlug, projectSlug)
  const { create: createIngressController, update: updateIngressController } = useIngressControllersResources(teamSlug, projectSlug)
  const { all: getAllNetworks, create: createNetwork, update: updateNetwork, destroy: destroyNetwork } = useProjectNetworksResources(teamSlug, projectSlug)
  const { all: getAllFiles, create: createFile, update: updateFile, destroy: destroyFile, edit: editFile } = useProjectFilesResources(teamSlug, projectSlug)
  const { nodesById } = useNodes()
  const { openConfirmationDialog } = useConfirmationDialog()
  const { destroy } = useProjectsResources(team.id)
  const [networks, setNetworks] = useState([])
  const [networksLoading, setNetworksLoading] = useState(true)
  const [files, setFiles] = useState([])
  const [filesLoading, setFilesLoading] = useState(true)

  const getTabValue = () => {
    if (location.pathname.endsWith('/ingress-controllers')) return 1
    if (location.pathname.endsWith('/deployments')) return 2
    if (location.pathname.endsWith('/yaml')) return 3
    if (location.pathname.endsWith('/networks')) return 4
    if (location.pathname.endsWith('/files')) return 5
    return 0
  }
  const tabValue = getTabValue()

  const loadNetworks = useCallback(() => {
    setNetworksLoading(true)
    getAllNetworks()
      .then((response) => {
        setNetworks(response.networks || [])
      })
      .catch(() => {
        setNetworks([])
      })
      .finally(() => {
        setNetworksLoading(false)
      })
  }, [getAllNetworks])

  const loadFiles = useCallback(() => {
    setFilesLoading(true)
    getAllFiles()
      .then((response) => {
        setFiles(response.projectFiles || [])
      })
      .catch(() => {
        setFiles([])
      })
      .finally(() => {
        setFilesLoading(false)
      })
  }, [getAllFiles])

  useEffect(() => {
    loadNetworks()
    loadFiles()
  }, [teamSlug, projectSlug])

  const projectNodes = useMemo(() => {
    const nodeIds = get(project, 'nodeIds', [])
    return compact(map(nodeIds, (id) => get(nodesById, id)))
  }, [project, nodesById])

  const handleDelete = useCallback(() => {
    openConfirmationDialog({
      title: <FormattedMessage id="dockerProjects.delete.title" />,
      message: <FormattedMessage id="dockerProjects.delete.message" />,
      onConfirm: () => destroy(project.id)
        .then(() => {
          navigate(`/teams/${teamSlug}/docker/projects`)
        })
        .catch(() => {
          enqueueSnackbar(formatMessage({ id: 'dockerProjects.delete.error' }), { variant: 'error' })
        }),
      onCancel: () => Promise.resolve(),
    })
  }, [openConfirmationDialog, destroy, project.id, navigate, teamSlug, enqueueSnackbar, formatMessage])

  if (loading) return <LoadingState />
  if (error) return <ErrorState messageId="dockerProjects.error" />

  const basePath = `/teams/${teamSlug}/docker/projects/${projectSlug}`

  const tabs = useMemo(() => [
    { label: <FormattedMessage id="dockerProjects.tabs.services" />, to: basePath },
    { label: <FormattedMessage id="dockerProjects.tabs.ingressControllers" />, to: `${basePath}/ingress-controllers` },
    { label: <FormattedMessage id="dockerProjects.tabs.deployments" />, to: `${basePath}/deployments` },
    { label: <FormattedMessage id="dockerProjects.tabs.yaml" />, to: `${basePath}/yaml` },
    { label: <FormattedMessage id="dockerProjects.tabs.networks" />, to: `${basePath}/networks` },
    { label: <FormattedMessage id="dockerProjects.tabs.files" />, to: `${basePath}/files` },
  ], [basePath])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <PageHeader
        title={project.name}
        entities={{ team, project }}
        actions={
          <Flex gap={1}>
            <Button
              component={RouterLink}
              to="edit"
              variant="outlined"
              size="small"
            >
              <FormattedMessage id="dockerProjects.editButton" />
            </Button>
            <Button
              onClick={handleDelete}
              variant="outlined"
              color="error"
              size="small"
            >
              <FormattedMessage id="dockerProjects.delete.button" />
            </Button>
          </Flex>
        }
      />
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        overflow: 'hidden',
        minHeight: 0
      }}>
        <ResponsiveTabs tabs={tabs} value={tabValue} />

        <PageBody>
          <TabPanel value={tabValue} index={0}>
            <ProjectServicesTab project={project} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ProjectIngressControllersTab
              ingressControllers={ingressControllers}
              services={services}
              networks={networks}
              loading={ingressControllersLoading || networksLoading}
              createIngressController={createIngressController}
              updateIngressController={updateIngressController}
              onSuccess={reloadIngressControllers}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ProjectDeploymentsTab
              deployments={deployments}
              nodes={projectNodes}
              loading={deploymentsLoading}
              createDeployment={createDeployment}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={3}>
            <ProjectYamlTab
              getCompose={getCompose}
              updateCompose={updateCompose}
              active={tabValue === 3}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={4}>
            <ProjectNetworksTab
              networks={networks}
              loading={networksLoading}
              createNetwork={createNetwork}
              updateNetwork={updateNetwork}
              destroyNetwork={destroyNetwork}
              onSuccess={loadNetworks}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={5}>
            <ProjectFilesTab
              files={files}
              loading={filesLoading}
              createFile={createFile}
              updateFile={updateFile}
              destroyFile={destroyFile}
              editFile={editFile}
              onSuccess={loadFiles}
            />
          </TabPanel>
        </PageBody>
      </Box>
    </Box>
  )
}

export default ProjectShowPage
